import { BUS_CONFIG } from '../../../config/bus.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import {
  COLLECTIBLE_FLOAT_HEIGHT,
  collectibleLanes,
  collectibleLine,
  entryConnector,
  laneArea,
  laneCenterX,
  moduleSides,
  standardLanes,
  straightExit,
} from '../segmentBuilders';
import { SegmentKind, type GroundSurface, type SegmentDefinition } from '../SegmentDefinition';

/**
 * ructec-Bulli (user 2026-10-05, BusConfig) – the drive is its own little game: traffic instead of obstacles (user:
 * "keine Hindernisse, nur die Rampen und die Straße … von hinten kommen Fahrzeuge, mit Gegenverkehr"):
 *
 *   SEG_BUS_START_40M   scheduled once at 1000 m (highest priority; golden ramp and dozer marks inside the ride are
 *                       left out): the van idles in the middle lane, the runner gets in on contact (BusManager).
 *   ride                only road modules follow (tag 'bus-road'): the modules below, curves, crossings, the ad gantry
 *                       (their obstacles are cleared inside the ride). The traffic is BusTraffic.
 *   SEG_BUS_ROAD_40M    open road, bottles in the lanes (the oncoming lane 0 pays the same – at a risk).
 *   SEG_BUS_KICKER_40M  two small kickers on lanes 1 and 2 – a hop with bottles in the air.
 *   SEG_BUS_RAMP_40M    a timber ramp on lanes 1 and 2 (1.08 m) – a jump with bottles in the air.
 *   SEG_BUS_FINALE_60M  the big ramp (1.62 m) shortly before the end of the ride (VehicleRideRules.finale), the longest
 *                       flight with a bottle arch.
 * The ramps leave the oncoming lane 0 flat: the oncoming traffic drives there, and the van may dodge a ramp through it.
 * On foot (thrown out when the load is gone) every module stays passable: the ramps are walkable slopes.
 */
const W = WORLD_CONFIG;
const LENGTH = 40;
const SIDES = moduleSides(LENGTH);
const ROAD_VISUAL = { visualAssetId: 'SEG_STRAIGHT_20M', extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }] } as const;
export const BUS_ROAD_TAG = 'bus-road';
export const BUS_FINALE_TAG = 'bus-finale';
const RIDE_ONLY = 'ride-only';
const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
/** The ramps lie on the van's lanes 1 and 2 – lane 0 is the oncoming traffic's. */
const RAMP_LANES = [1, 2] as const;
/** Centre of the ramp lanes (the ramp models are built centred there). */
export const RAMP_X = (laneCenterX(W, RAMP_LANES[0]) + laneCenterX(W, RAMP_LANES[1])) / 2;

// ── start ──────────────────────────────────────────────────────────────────

export const BUS_START = { lane: 1, parkZ: 26 } as const;

export const SEG_BUS_START_40M: SegmentDefinition = {
  id: 'SEG_BUS_START_40M',
  kind: SegmentKind.Straight,
  displayName: 'ructec-Bulli – Einstieg 40 m',
  length: LENGTH,
  width: W.trackWidth,
  ...ROAD_VISUAL,
  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(W),
  bus: BUS_START,
  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  // bottles lead into the middle lane, up to the van's rear
  collectibleSpawnPoints: collectibleLine(W, 'BUS_INVITE', [
    { lane: 1, from: 2, to: BUS_START.parkZ - BUS_CONFIG.body.rear - BUS_CONFIG.seat.z - 1, spacing: 2, height: flat },
  ]),
  ...SIDES,
  generation: {
    weight: 1,
    minDistance: 8000,
    minSegmentsAfterJunction: 1,
    family: 'BUS',
    // from 8000 m (user 2026-10-05: "erst ab 8000, 9000 Meter" – was at 1000 m), then every 8000 m; highest priority – the
    // golden ramp (1) and the dozer (0) give way
    schedule: { stretches: [{ from: 8000, every: 8000 }], window: 300, forceAfter: 60, sightLine: 0, priority: 2 },
    ride: {
      length: BUS_CONFIG.rideLength + BUS_CONFIG.runOut,
      roadTag: BUS_ROAD_TAG,
      // the big ramp starts ≈ 150 m before the ride ends (around the calm end of the traffic)
      finale: { tag: BUS_FINALE_TAG, before: BUS_CONFIG.runOut + 150 },
    },
    tags: ['setpiece', 'bus', 'vehicle'],
  },
};

// ── open road ──────────────────────────────────────────────────────────────

export const SEG_BUS_ROAD_40M: SegmentDefinition = {
  id: 'SEG_BUS_ROAD_40M',
  kind: SegmentKind.Straight,
  displayName: 'ructec-Bulli – Landstraße 40 m',
  length: LENGTH,
  width: W.trackWidth,
  ...ROAD_VISUAL,
  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(W),
  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  collectibleSpawnPoints: collectibleLanes(W, 1, LENGTH - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),
  ...SIDES,
  generation: {
    weight: 1.4,
    minDistance: 0,
    tags: ['straight', BUS_ROAD_TAG, RIDE_ONLY],
  },
};

// ── ramps ──────────────────────────────────────────────────────────────────

interface RampSpec {
  readonly id: string;
  readonly zFrom: number;
  readonly zTo: number;
  readonly height: number;
}

const rampSurface = (spec: RampSpec): GroundSurface => ({
  id: spec.id,
  ...laneArea(W, RAMP_LANES[0], RAMP_LANES[1], spec.zFrom, spec.zTo),
  heightFrom: 0,
  heightTo: spec.height,
});

/** Bottles in the air behind a lip – only an airborne van reaches them (inside its flight: reachable). */
const airBottles = (id: string, from: number, to: number, height: (z: number) => number) =>
  RAMP_LANES.flatMap((lane) => collectibleLine(W, `${id}_L${lane}`, [{ lane, from, to, spacing: 1.5, height }]));

export const BUS_RAMP: RampSpec = { id: 'SURF_BUS_RAMP', zFrom: 15, zTo: 21, height: 1.08 };

export const SEG_BUS_RAMP_40M: SegmentDefinition = {
  id: 'SEG_BUS_RAMP_40M',
  kind: SegmentKind.Straight,
  displayName: 'ructec-Bulli – Rampe 40 m',
  length: LENGTH,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
    { assetId: 'ENV_BUS_RAMP_A', position: { x: RAMP_X, y: 0, z: BUS_RAMP.zFrom } },
  ],
  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(W),
  terrain: { surfaces: [rampSurface(BUS_RAMP)], gaps: [], ceilings: [] },
  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  collectibleSpawnPoints: [...collectibleLanes(W, 1, BUS_RAMP.zFrom - 1, 2, COLLECTIBLE_FLOAT_HEIGHT), ...airBottles('BUS_AIR', BUS_RAMP.zTo + 1.5, BUS_RAMP.zTo + 7.5, () => 2.6)],
  ...SIDES,
  generation: {
    weight: 0.5,
    minDistance: 0,
    cooldownDistance: 120,
    family: 'BUS_RAMP',
    tags: ['straight', 'ramp', BUS_ROAD_TAG, RIDE_ONLY],
  },
};

export const BUS_KICKERS: readonly RampSpec[] = [
  { id: 'SURF_BUS_KICKER_1', zFrom: 8, zTo: 11, height: 0.5 },
  { id: 'SURF_BUS_KICKER_2', zFrom: 26, zTo: 29, height: 0.5 },
];

export const SEG_BUS_KICKER_40M: SegmentDefinition = {
  id: 'SEG_BUS_KICKER_40M',
  kind: SegmentKind.Straight,
  displayName: 'ructec-Bulli – Kicker 40 m',
  length: LENGTH,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
    ...BUS_KICKERS.map((k) => ({ assetId: 'ENV_BUS_KICKER_A', position: { x: RAMP_X, y: 0, z: k.zFrom } })),
  ],
  entry: entryConnector(),
  exits: [straightExit(LENGTH)],
  lanes: standardLanes(W),
  terrain: { surfaces: BUS_KICKERS.map(rampSurface), gaps: [], ceilings: [] },
  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  collectibleSpawnPoints: BUS_KICKERS.flatMap((k, i) => airBottles(`BUS_HOP_${i + 1}`, k.zTo + 1.5, k.zTo + 6, () => 2.4)),
  ...SIDES,
  generation: {
    weight: 0.6,
    minDistance: 0,
    cooldownDistance: 80,
    family: 'BUS_KICKER',
    tags: ['straight', 'ramp', BUS_ROAD_TAG, RIDE_ONLY],
  },
};

const FINALE_LENGTH = 60;
export const BUS_FINALE: RampSpec = { id: 'SURF_BUS_FINALE', zFrom: 12, zTo: 21, height: 1.62 };
/**
 * The arch of bottles over the flight path – reachable (user 2026-10-05: "die Flaschen müssen erreichbar sein"): off the
 * 1.62 m lip at ≈ 15 m/s the van climbs ≈ 0.3 m more and lands ≈ 12 m on; its body (2.4 m high) sweeps 2.0 … 4.3 m at the
 * top. The arch stays inside that flight (12 m, 2.3 … 3.3 m).
 */
const FINALE_FLIGHT = 12;
const finaleArch = (z: number): number => 2.3 + 1.0 * Math.sin(Math.PI * Math.min(1, Math.max(0, (z - BUS_FINALE.zTo) / FINALE_FLIGHT)));

export const SEG_BUS_FINALE_60M: SegmentDefinition = {
  id: 'SEG_BUS_FINALE_60M',
  kind: SegmentKind.Straight,
  displayName: 'ructec-Bulli – Finale 60 m',
  length: FINALE_LENGTH,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 40 } },
    { assetId: 'ENV_BUS_FINALE_RAMP_A', position: { x: RAMP_X, y: 0, z: BUS_FINALE.zFrom } },
  ],
  entry: entryConnector(),
  exits: [straightExit(FINALE_LENGTH)],
  lanes: standardLanes(W),
  terrain: { surfaces: [rampSurface(BUS_FINALE)], gaps: [], ceilings: [] },
  obstacleSpawnPoints: [],
  obstacleRowLayouts: { sparse: [], dense: [] },
  collectibleSpawnPoints: [...collectibleLanes(W, 1, BUS_FINALE.zFrom - 1, 2, COLLECTIBLE_FLOAT_HEIGHT), ...airBottles('BUS_FINALE', BUS_FINALE.zTo + 1, BUS_FINALE.zTo + FINALE_FLIGHT - 1, finaleArch)],
  ...moduleSides(FINALE_LENGTH),
  generation: {
    weight: 1,
    minDistance: 0,
    tags: ['straight', 'ramp', BUS_ROAD_TAG, BUS_FINALE_TAG, RIDE_ONLY],
  },
};

export const BUS_SEGMENTS: readonly SegmentDefinition[] = [SEG_BUS_START_40M, SEG_BUS_ROAD_40M, SEG_BUS_KICKER_40M, SEG_BUS_RAMP_40M, SEG_BUS_FINALE_60M];
